/**
 * rsvp.ts — FormCarry fetch helper for meetup RSVPs (RSVP v1)
 *
 * POSTs an RSVP to the same public FormCarry endpoint as the newsletter form
 * (form 6NHpuKnjjj1) with discriminator fields so submissions can be filtered
 * in the FormCarry log:
 *   - type: 'rsvp'            — distinguishes RSVPs from newsletter signups
 *   - event: e.g. '2026-09-10' — which meetup the RSVP is for
 *
 * v1 deliberately reuses the existing form (no new infra, no secrets — D-10
 * still holds). When a dedicated RSVP form exists in FormCarry, only FORM_ID
 * here needs to change. Result semantics mirror subscribe.ts exactly:
 * success gates on data.code === 200 (D-05); the honeypot _gotcha value is
 * forwarded so FormCarry drops bot submissions server-side (BACK-02).
 */

import type { SubscribeResult } from './subscribe';

// Same public endpoint as subscribe.ts — not a secret (D-10)
const FORM_ID = '6NHpuKnjjj1';

const ENDPOINT = `https://formcarry.com/s/${FORM_ID}`;

const SERVER_FALLBACK = 'Something went wrong. Please try again.';

export type RsvpResult = SubscribeResult;

export async function rsvp(
  name: string,
  email: string,
  event: string,
  gotcha: string = ''
): Promise<RsvpResult> {
  try {
    const response = await fetch(ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      body: JSON.stringify({ name, email, type: 'rsvp', event, _gotcha: gotcha }),
    });

    let data: {
      code?: number;
      status?: string;
      message?: string;
      errors?: Record<string, { message: string; rule: string }>;
    } = {};

    try {
      data = (await response.json()) as typeof data;
    } catch {
      // Non-JSON body — classify by HTTP status below
    }

    if (data.code === 200) {
      return { kind: 'success' };
    }

    if (data.code === 422 || response.status === 422) {
      return {
        kind: 'validation',
        message: data.message ?? 'Please enter a valid email address.',
      };
    }

    return {
      kind: 'server',
      message: data.message ?? SERVER_FALLBACK,
    };
  } catch {
    return { kind: 'server', message: SERVER_FALLBACK };
  }
}
